import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiServer, FiCloud, FiMonitor, FiShield, FiTrendingUp, FiAlertTriangle, FiCpu, FiHardDrive } from 'react-icons/fi';
import './InfrastructureMonitoring.css';

const InfrastructureMonitoring = () => {
  const features = [
    {
      icon: <FiServer />,
      title: "End-to-end Monitoring",
      description: "Monitor infrastructure components across various tech stacks with comprehensive visibility."
    },
    {
      icon: <FiCloud />,
      title: "Cloud and On-premise Support",
      description: "Support both cloud and on-premise deployments with flexible monitoring capabilities."
    },
    {
      icon: <FiMonitor />,
      title: "Instant Observability",
      description: "Get instant observability across your entire stack with a single sensor setup in minutes."
    },
    {
      icon: <FiShield />,
      title: "AI-Powered Analytics",
      description: "AI analyzes infrastructure telemetry data to identify issues and provide recommendations."
    },
    {
      icon: <FiTrendingUp />,
      title: "Cost Optimization",
      description: "Reduce infrastructure monitoring costs by up to 10x with efficient data processing."
    },
    {
      icon: <FiAlertTriangle />,
      title: "Intelligent Alerting",
      description: "AI-powered alerts and error tracking to reduce alert fatigue and accelerate resolution."
    }
  ];

  const benefits = [
    "End-to-end monitoring across various tech stacks",
    "Cloud and on-premise deployment support",
    "Instant observability with single sensor setup",
    "AI-powered infrastructure analytics and insights",
    "Up to 10x cost reduction in monitoring",
    "Intelligent alerting to reduce alert fatigue"
  ];

  return (
    <div className="infrastructure-monitoring-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Infrastructure Monitoring</h1>
            <p className="hero-subtitle">
              End-to-end monitoring across various tech stacks with comprehensive infrastructure visibility
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Comprehensive Infrastructure Monitoring</h2>
            <p>Monitor your entire infrastructure stack with AI-powered insights and analytics</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Infrastructure Monitoring?</h2>
              <p>
                Our platform provides comprehensive monitoring across your entire infrastructure 
                stack with AI-powered analytics for proactive issue resolution.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiServer />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Infrastructure Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiCpu />
                    <span>CPU Usage: 65%</span>
                  </div>
                  <div className="metric">
                    <FiHardDrive />
                    <span>Disk Usage: 78%</span>
                  </div>
                  <div className="metric">
                    <FiServer />
                    <span>Active Servers: 24</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Monitor Your Infrastructure?</h2>
            <p>Get comprehensive visibility into your infrastructure with AI-powered insights.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InfrastructureMonitoring;
